import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, PlayCircleIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-screen bg-dark overflow-hidden" data-id="element-196">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(201,168,76,0.08)_0%,transparent_50%)]" data-id="element-197" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex items-center" data-id="element-198">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center w-full pt-28 pb-20 lg:pt-0 lg:pb-0" data-id="element-199">
          {/* Left — Text Content */}
          <div className="order-2 lg:order-1" data-id="element-200">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex items-center gap-3 mb-8" data-id="element-201">
              <div className="h-[1px] w-10 bg-gold" data-id="element-202" />
              <span className="text-gold font-semibold tracking-[0.3em] text-xs uppercase" data-id="element-203">
                Apostle · Pastor · Author
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="font-playfair font-bold text-white leading-[1.05] tracking-tight mb-6" data-id="element-204">
              <span className="block text-5xl md:text-6xl lg:text-7xl xl:text-8xl" data-id="element-205">
                Apostle
              </span>
              <span className="block text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-transparent bg-clip-text bg-gradient-to-r from-gold via-goldLight to-gold" data-id="element-206">
                Akintunde
              </span>
              <span className="block text-5xl md:text-6xl lg:text-7xl xl:text-8xl" data-id="element-207">
                Phillips
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 0.7
          }} className="text-white/60 font-inter font-light text-lg md:text-xl max-w-lg mb-10 leading-relaxed" data-id="element-208">
              Reviving and restoring the love of God in the heart of men through
              prophetic ministry, discipleship, and uncompromising truth.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.9
          }} className="flex flex-col sm:flex-row items-start gap-4" data-id="element-209">
              <a href="#messages" className="inline-flex items-center gap-3 px-8 py-4 bg-gold hover:bg-goldLight text-dark font-semibold tracking-wide transition-all duration-300 rounded-sm" data-id="element-210">
                <PlayCircleIcon className="w-5 h-5" data-id="element-211" />
                Watch Messages
              </a>
              <a href="#contact" className="px-8 py-4 border border-white/20 text-white hover:border-gold hover:text-gold font-semibold tracking-wide transition-all duration-300 rounded-sm" data-id="element-212">
                Send Prayer Request
              </a>
            </motion.div>

            {/* Credential / Church Info */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.8,
            delay: 1.2
          }} className="mt-14 pt-8 border-t border-white/10" data-id="element-213">
              <p className="text-white/40 text-xs font-inter uppercase tracking-[0.2em] mb-3" data-id="element-214">
                Senior Pastor
              </p>
              <p className="text-white/70 font-playfair text-lg italic" data-id="element-215">
                Grace of God Ministries
              </p>
            </motion.div>
          </div>

          {/* Right — Photo */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }} className="order-1 lg:order-2 relative flex justify-center lg:justify-end" data-id="element-216">
            {/* Decorative elements */}
            <div className="absolute -top-8 -right-8 w-72 h-72 bg-gold/5 rounded-full blur-3xl" data-id="element-217" />
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-gold/10 rounded-full blur-2xl" data-id="element-218" />

            {/* Gold accent frame */}
            <div className="absolute top-6 right-6 bottom-6 left-6 border border-gold/20 rounded-sm pointer-events-none z-10" data-id="element-219" />

            <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl" data-id="element-220">
              <div className="aspect-[3/4] overflow-hidden rounded-sm shadow-2xl" data-id="element-221">
                <img src="https://cdn.magicpatterns.com/uploads/wsZabRN8ymxvHzMJMUaiEG/Apostlephillipsbookcoversession15of30-683x1024.jpg" alt="Apostle Akintunde Phillips" className="w-full h-full object-cover object-top" data-id="element-222" />
              </div>

              {/* Floating accent card */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.7,
              delay: 1.4
            }} className="absolute -bottom-6 -left-6 bg-charcoal border border-gold/30 px-6 py-4 rounded-sm shadow-xl" data-id="element-223">
                <p className="text-gold font-playfair font-bold text-2xl" data-id="element-224">
                  20+
                </p>
                <p className="text-white/60 font-inter text-xs tracking-wide uppercase" data-id="element-225">
                  Years of Ministry
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 1.5
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" data-id="element-226">
        <span className="text-white/30 text-xs tracking-[0.2em] uppercase" data-id="element-227">
          Scroll
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-228">
          <ChevronDownIcon className="w-5 h-5 text-gold/60" data-id="element-229" />
        </motion.div>
      </motion.div>
    </section>;
}